import AutoSwipePanels from '../components/AutoSwipePanels';
import React from 'react';
import { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { io } from 'socket.io-client';
import { useSelector, useDispatch } from "react-redux";
import axios from 'axios';
import { getApiBaseUrl_dynamic, getSocketUrl_dynamic } from '../config/config.js';

import {
    getAnnouncementData,
    setAnnouncementData,
} from "../Redux/Actions/Announcement/index.js";

// Helper to get axios instance with current dynamic base URL
const getAxios = () => axios.create({ baseURL: getApiBaseUrl_dynamic() });

const Announcement = ({ fontSize = 48, accessibilityWidth, wing }) => {

    const dispatch = useDispatch();

    const [Emergency, setEmergency] = useState([]);
    const [tableData, settableData] = useState([]);
    const [number, setNumber] = useState([]);
    const [accessibilityImageLoaded, setAccessibilityImageLoaded] = useState(false);

    const Announcement = useSelector((state) => state.Announcement.Announcement);
    const Airport = useSelector((state) => state.Airport.Airport);

    // Use wing prop if provided, otherwise fall back to Airport.wing from Redux
    const currentWing = wing || Airport?.wing;

    // Socket.IO subscription for real-time announcement updates
    useEffect(() => {
        const socketUrl = getSocketUrl_dynamic();
        const socket = io(socketUrl || window.location.origin, {
            path: '/socket.io',
            transports: ['websocket', 'polling'],
        });

        socket.on('Announcement', (data) => {
            try {
                const parsed = typeof data === 'string' ? JSON.parse(data) : data;
                dispatch(setAnnouncementData(parsed));
            } catch (e) {
                console.error('Error parsing Announcement socket data:', e);
            }
        });

        return () => {
            socket.disconnect();
        };
    }, [dispatch]);

    useEffect(() => {
        // Fetch announcements and poll for updates (admin-controlled data)
        console.log('Fetching announcement data');
        dispatch(getAnnouncementData());

        // Poll every 5 minutes (announcements change infrequently)
        const pollInterval = setInterval(() => {
            dispatch(getAnnouncementData());
        }, 300000); // 5 minutes (reduced from 30 seconds)

        return () => clearInterval(pollInterval);

    }, [dispatch])

    // Fetch accessibility numbers with reduced polling
    useEffect(() => {
        const fetchNumbers = () => {
            getAxios().get('/api/number').then((res) => {
                if (res.data.success) {
                    // Filter numbers by airport/wing
                    const filteredNumbers = res.data.data.filter(val =>
                        val.AirportInfo.airport === Airport.airport &&
                        val.AirportInfo.wing === Airport.wing
                    );
                    setNumber(filteredNumbers);
                }
            }).catch(err => {
                console.error('Error fetching accessibility numbers:', err);
            });
        };

        fetchNumbers();

        // Poll every 5 minutes (accessibility numbers change infrequently)
        const numberPollInterval = setInterval(() => {
            fetchNumbers();
        }, 300000); // 5 minutes (reduced from 30 seconds)

        return () => clearInterval(numberPollInterval);

    }, [Airport])

    useEffect(() => {
        console.log('📢 Announcement filter debug:', {
            announcementCount: Announcement.length,
            announcementSample: Announcement?.[0],
            currentAirport: Airport?.airport,
            currentWing: currentWing,
            wingProp: wing,
            reduxWing: Airport?.wing
        });

        // Log ALL announcements with their wing values BEFORE filtering
        if (Announcement.length > 0) {
            console.log('📋 ALL Announcements in database:', Announcement.map(v => ({
                airport: v?.AirportInfo?.airport,
                wing: v?.AirportInfo?.wing,
                status: v?.Stauts,
                topic: v?.Topic,
                post: v?.Post?.substring(0, 30)
            })));
        }

        if (Announcement.length !== 0) {
            // Filter by airport/wing and exclude expired announcements
            let nwArr = Announcement.filter(val => {
                const matches = val.AirportInfo.airport === Airport.airport &&
                               val.AirportInfo.wing === currentWing &&
                               val.Stauts === "Actived";

                // Log each announcement being checked
                if (val.AirportInfo.airport === Airport.airport) {
                    console.log(`🔍 Checking announcement: "${val.Topic?.substring(0, 20)}"`, {
                        dbWing: val.AirportInfo.wing,
                        expectedWing: currentWing,
                        wingMatch: val.AirportInfo.wing === currentWing,
                        status: val.Stauts,
                        matches: matches
                    });
                }

                return matches;
            })

            console.log('🎯 Filtered announcements:', {
                total: Announcement.length,
                filtered: nwArr.length,
                details: nwArr.map(v => ({ airport: v?.AirportInfo?.airport, wing: v?.AirportInfo?.wing, status: v?.Stauts, type: v?.Type, post: v?.Post?.substring(0, 50) }))
            });

            const regularAnnouncements = nwArr.filter((val) => val.Type !== "Emergency");
            const emergencyAnnouncements = nwArr.filter((val) => val.Type === "Emergency");

            settableData(regularAnnouncements);
            setEmergency(emergencyAnnouncements);

            console.log('📊 Final announcements:', {
                regular: regularAnnouncements.length,
                emergency: emergencyAnnouncements.length,
                regularData: regularAnnouncements.map(v => v.Post?.substring(0, 30))
            });

            if (regularAnnouncements.length === 0 && emergencyAnnouncements.length === 0) {
                console.warn('⚠️ No announcements match the current airport/wing filter!');
                console.warn('💡 Check admin panel to ensure announcements are configured for:', {
                    airport: Airport?.airport,
                    wing: Airport?.wing
                });
            }
        }
        else {
            console.log('⚠️ No announcements in state');
            // Filter to only show active announcements
            settableData(Announcement.filter((val) => val.Type !== "Emergency" && val.Stauts === "Actived"));
            setEmergency(Announcement.filter((val) => val.Type === "Emergency" && val.Stauts === "Actived"));
        }
    }, [Announcement, Airport, currentWing])

    // Preload accessibility image
    useEffect(() => {
        const img = new Image();
        const url = `${process.env.PUBLIC_URL}/image/Accessibility-new.png`;
        img.onload = () => setAccessibilityImageLoaded(true);
        img.onerror = () => setAccessibilityImageLoaded(false);
        img.src = url;
    }, []);

    // Calculate responsive sizes based on passed fontSize prop (scales with screen)
    const containerHeight = Math.max(60, Math.min(fontSize * 2.5, fontSize * 1.5));
    const emergencyTitleFont = Math.round(fontSize * 1.0);
    const specialTitleFont = Math.round(fontSize * 0.7);
    const marqueeFont = Math.round(fontSize * 0.9);
    const annBorder = Math.max(3, Math.round(fontSize * 0.07)); // 3px at 44, 6px at 89

    return (
        <div style={{ display: "flex", height: '100%', placeItems: 'center', boxSizing: 'border-box' }}>
            {
                Emergency.length !== 0 ?
                    <Box sx={{ flex: 2, backgroundColor: 'black', color: 'white', zIndex: '1', height: '100%', boxSizing: 'border-box' }}>
                        <div style={{ border: `${annBorder}px solid white`, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>
                            <div style={{ fontWeight: 'bold', fontSize: `${emergencyTitleFont}px`, textAlign: 'center', width: '100%' }}>Emergency</div>
                        </div>
                    </Box> :
                    <Box sx={{ flex: 2, backgroundColor: 'black', color: 'white', height: '100%', boxSizing: 'border-box' }}>
                        <div style={{ border: `${annBorder}px solid white`, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', boxSizing: 'border-box' }}>
                            <div style={{ fontWeight: 'bold', fontSize: `${specialTitleFont}px`, textAlign: 'center', width: '100%' }}>Special</div>
                            <div style={{ fontWeight: 'bold', fontSize: `${specialTitleFont}px`, textAlign: 'center', width: '100%' }}>Announcement</div>
                        </div>
                    </Box>
            }
            {
                Emergency.length !== 0 ?
                    <Box sx={{ flex: 6, backgroundColor: "red", color: 'white', height: '100%', overflowY: "hidden", display: "flex", alignItems: "center", boxSizing: 'border-box' }}>
                        <div className="marquee-container">
                            <div className="marquee-content">
                                <span style={{ fontWeight: 'bold', fontSize: `${marqueeFont}px`, paddingRight: `${Math.round(fontSize * 3)}px` }}>{Emergency[0].Post}</span>
                                <span style={{ fontWeight: 'bold', fontSize: `${marqueeFont}px`, paddingRight: `${Math.round(fontSize * 3)}px` }}>{Emergency[0].Post}</span>
                            </div>
                        </div>
                    </Box>
                    :
                    <Box sx={{ flex: 6, backgroundColor: "black", color: 'white', overflowY: "hidden", display: "flex", alignItems: "center", height: '100%', border: `${annBorder}px solid white`, boxSizing: 'border-box' }}>
                        {tableData.length > 0 ? (
                            <div className="marquee-container">
                                <div className="marquee-content">
                                    {tableData.map(({ Post }, index) =>
                                        <span key={index} style={{ marginRight: `${Math.round(fontSize * 1.14)}px`, fontSize: `${marqueeFont}px`, fontWeight: 'bold' }}>{Post}</span>
                                    )}
                                    <span style={{ marginRight: `${Math.round(fontSize * 3)}px` }}></span>
                                    {tableData.map(({ Post }, index) =>
                                        <span key={`dup-${index}`} style={{ marginRight: `${Math.round(fontSize * 1.14)}px`, fontSize: `${marqueeFont}px`, fontWeight: 'bold' }}>{Post}</span>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div style={{ width: '100%', height: '100%' }}></div>
                        )}
                    </Box>
            }
                <AutoSwipePanels interval={15000} style={{ width: accessibilityWidth ? `${accessibilityWidth}px` : 'calc((100vh - 200px) / 2)', height: '100%', overflow: "hidden", boxSizing: 'border-box', flexShrink: 0 }}>
                    <div style={{ overflow: "hidden", border: `${annBorder}px solid white`, height: '100%', boxSizing: 'border-box' }}>
                        {accessibilityImageLoaded ? (
                            <img src={`${process.env.PUBLIC_URL}/image/Accessibility-new.png`} alt="" style={{ objectFit: 'fill', height: '100%', width: '100%', zIndex: '1' }} />
                        ) : (
                            <div style={{ color: '#666', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'black' }}>Loading...</div>
                        )}
                    </div>
                    {
                        number.map((val, index) =>
                            <div key={index} style={{ height: '100%', overflow: "hidden", border: `${annBorder}px solid white`, backgroundColor: 'black', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>
                                <p style={{ fontSize: `${marqueeFont}px`, fontWeight: 'bold', backgroundColor: 'black', textAlign: "center", margin: 0 }}>{val.Number}</p>
                            </div>
                        )
                    }

                </AutoSwipePanels>

        </div >
    )
}

export default Announcement;